import { TextField } from '@getsynapse/design-system';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

const formatCurrency = (number: number | string): string => {
  if (!number) {
    return '0.00';
  }

  if (typeof number === 'string') {
    number = parseFloat(number);
  }

  return number.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseCurrency = (value: string): string => {
  return value.replace(/[^0-9.]/g, '');
};

type CurrencyFieldProps = {
  value: number | string;
  symbol?: string;
  [k: string]: any;
};

const CurrencyField = ({
  value,
  symbol = '$',
  ...props
}: CurrencyFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [rawValue, setRawValue] = useState(value);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const cleaned = parseCurrency(e.target.value);
    setRawValue(cleaned);
    if (props.onChange) {
      props.onChange(e);
    }
  };

  const handleFocus = (e: FocusEvent) => {
    setIsFocused(true);
    setTimeout(() => {
      inputRef.current?.select();
    }, 0);
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const handleBlur = (e: FocusEvent) => {
    setIsFocused(false);
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  useEffect(() => {
    setRawValue(value);
  }, [value]);

  return (
    <TextField
      inputMode='decimal'
      {...props}
      value={isFocused ? rawValue : symbol + '' + formatCurrency(rawValue)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      // Invariant (CGP-213): passing a ref also keeps the design-system
      // TextField out of its overflow-Tooltip mode, which clobbers the
      // input's onBlur/onKeyDown. Do not remove without rechecking
      // save-on-blur for values wider than the field.
      ref={inputRef}
    />
  );
};

export default CurrencyField;
